import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const ROLES = [["admin", "Organisation Admin"], ["privacy", "Privacy / DPO"], ["legal", "Legal"], ["security", "Security"], ["engineering", "Engineering"], ["auditor", "Auditor"]];

const Multi = ({ label, options, value, onChange, k }) => (
  <div className="space-y-1"><Label className="text-xs">{label} <span className="font-normal text-slate-400">(none = all)</span></Label>
    <div className="flex flex-wrap gap-x-3 gap-y-1">{options.map(([v, l]) => (
      <label key={v} className="flex items-center gap-1 text-xs"><Checkbox checked={value.includes(v)} onCheckedChange={(on) => onChange(on ? [...value, v] : value.filter((x) => x !== v))} data-testid={`scope-${k}-${v}`} />{l}</label>))}</div>
  </div>
);

export function PolicyDialog({ policy, meta, sources, onClose, onSaved }) {
  const [p, setP] = useState(null);
  useEffect(() => { if (policy) setP({ name: "", description: "", priority: 100, active: true, rules: { ...meta.default_rules }, ...policy, scope: { roles: [], source_ids: [], environments: [], purposes: [], ...(policy.scope || {}) } }); }, [policy, meta]);
  if (!p) return null;
  const setScope = (k, v) => setP({ ...p, scope: { ...p.scope, [k]: v } });
  const save = async () => {
    const body = { name: p.name, description: p.description, rules: p.rules, scope: p.scope, priority: Number(p.priority), active: p.active };
    try { p.id ? await api.put(`/protection/policies/${p.id}`, body) : await api.post("/protection/policies", body); toast.success("Policy saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!policy} onOpenChange={(o) => !o && (setP(null), onClose())}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="policy-dialog">
        <DialogHeader><DialogTitle>{p.id ? "Edit" : "New"} PII protection policy</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Name</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="policy-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Priority (lower wins)</Label><Input type="number" value={p.priority} onChange={(e) => setP({ ...p, priority: e.target.value })} data-testid="policy-priority" /></div>
        </div>
        <label className="flex items-center gap-2 text-xs"><Switch checked={p.active} onCheckedChange={(v) => setP({ ...p, active: v })} data-testid="policy-active" />Active</label>
        <div className="grid gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-2">
          <Multi k="roles" label="User roles" options={ROLES} value={p.scope.roles} onChange={(v) => setScope("roles", v)} />
          <Multi k="env" label="Environments" options={meta.environments.map((e) => [e, e])} value={p.scope.environments} onChange={(v) => setScope("environments", v)} />
          <Multi k="purpose" label="Purposes" options={meta.purposes.map((e) => [e, e])} value={p.scope.purposes} onChange={(v) => setScope("purposes", v)} />
          <Multi k="source" label="Data sources" options={sources.map((s) => [s.id, s.name])} value={p.scope.source_ids} onChange={(v) => setScope("source_ids", v)} />
        </div>
        <div className="grid gap-2 sm:grid-cols-2" data-testid="policy-rules">
          {meta.categories.map((c) => (
            <div key={c.key} className="flex items-center justify-between gap-2 rounded border border-slate-100 px-2 py-1">
              <span className="text-xs text-slate-700">{c.label}</span>
              <Select value={p.rules[c.key] || "full"} onValueChange={(v) => setP({ ...p, rules: { ...p.rules, [c.key]: v } })}>
                <SelectTrigger className="h-7 w-48 text-xs" data-testid={`rule-${c.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>{meta.methods.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>))}
        </div>
        <DialogFooter><Button onClick={save} disabled={p.name.trim().length < 2} className="bg-blue-600 hover:bg-blue-700" data-testid="policy-save">Save policy</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
